/**
 * Which URLs exist, and what each one says about itself.
 *
 * `publicUrls` is the list of pages worth crawling; `metaForPath` turns any
 * path at all — public, private or nonexistent — into a `PageMeta`. The
 * prerenderer walks the first and resolves through the second, and
 * `sitemap.xml` is generated from the first, which is what guarantees the
 * sitemap can never advertise a page that answers `noindex`. A test asserts
 * exactly that.
 */

import { FEATURES } from '@yuan-math/config';
import type { CurriculumRegistry } from '@yuan-math/curriculum';
import type { PageMeta, SitemapEntry } from './types.ts';
import { SITE } from './site.ts';
import {
  aboutMeta,
  conceptMeta,
  courseMeta,
  coursesMeta,
  diagnosticMeta,
  homeMeta,
  lessonMeta,
  mapMeta,
  notFoundMeta,
  practiceMeta,
  pricingMeta,
  privacyMeta,
  privateMeta,
} from './pages.ts';

/**
 * The public pages that are not derived from content.
 *
 * Priority is a hint about relative importance within this site and nothing
 * more; it does not raise a ranking. The ordering it encodes is the honest one:
 * the map and the courses are the product, the legal pages are not.
 */
const STATIC_ENTRIES: SitemapEntry[] = [
  { path: '/', priority: 1.0, changefreq: 'weekly' },
  { path: '/map', priority: 0.9, changefreq: 'weekly' },
  { path: '/courses', priority: 0.9, changefreq: 'weekly' },
  { path: '/diagnostic', priority: 0.8, changefreq: 'monthly' },
  { path: '/about', priority: 0.5, changefreq: 'monthly' },
  // Only when there is something to sell. `App` routes `/pricing` behind
  // `FEATURES.payments`, and a sitemap naming a URL the app does not serve
  // sends crawlers — and anyone reading the sitemap — to a 404.
  ...(FEATURES.payments
    ? [{ path: '/pricing', priority: 0.5, changefreq: 'monthly' } as SitemapEntry]
    : []),
  { path: '/privacy', priority: 0.3, changefreq: 'yearly' },
];

/**
 * Every public URL, in crawl order.
 *
 * Derived from the registry rather than maintained by hand, so it cannot drift
 * from the content. A `planned` course is left out — an announced course with
 * no units is an empty result for whoever clicked it — but a `planned` concept
 * is included, at a lower priority, because that page does say something true
 * about where the mathematics goes.
 */
export function publicUrls(registry: CurriculumRegistry): SitemapEntry[] {
  const entries: SitemapEntry[] = [...STATIC_ENTRIES];

  for (const course of registry.curriculum.courses) {
    if (course.status !== 'available') continue;
    entries.push({ path: `/courses/${course.id}`, priority: 0.8, changefreq: 'monthly' });
  }

  for (const lesson of registry.curriculum.lessons) {
    entries.push({ path: `/lessons/${lesson.id}`, priority: 0.7, changefreq: 'monthly' });
  }

  for (const concept of registry.concepts) {
    entries.push({
      path: `/concepts/${concept.id}`,
      priority: concept.status === 'available' ? 0.6 : 0.3,
      changefreq: 'monthly',
    });
  }

  return entries;
}

/* -------------------------------------------------------------------------- */
/* The application's own pages                                                */
/* -------------------------------------------------------------------------- */

/**
 * Titles for the routes that belong to a person rather than to the public.
 *
 * All of them are `noindex`. They are listed here anyway so that a browser tab
 * and a history entry say which page it is, and so that the prerenderer and
 * the running app agree about a route neither of them indexes.
 */
export const APP_PAGES = {
  '/home': { title: 'Your learning', description: 'What to study next, your review queue and today’s quests.' },
  '/progress': { title: 'Your progress', description: 'Mastery by strand, your review schedule and where you are stuck.' },
  '/achievements': { title: 'Your badges', description: 'Streaks, levels and the badges you have earned.' },
  '/parent': { title: 'Parent view', description: 'How your child is doing, without a log of every answer.' },
  '/teach': { title: 'Teacher dashboard', description: 'Who is stuck, and on what.' },
  '/admin': { title: 'Administration', description: 'The state of the platform, and comped subscriptions.' },
  '/account': { title: 'Your account', description: 'Your sign-in details, plan and connected devices.' },
  '/start': { title: 'Start learning', description: 'Set up a learner and choose where to begin.' },
  '/register': { title: 'Create an account', description: 'Create a Yuan Math account. A child account needs a guardian.' },
  '/signin': { title: 'Sign in', description: 'Sign in to Yuan Math.' },
  '/activate': { title: 'Activate your account', description: 'Confirm your email address.' },
  '/consent': { title: 'Guardian consent', description: 'Approve a child’s account.' },
  '/forgot': { title: 'Reset your password', description: 'Send a password reset link.' },
  '/reset': { title: 'Choose a new password', description: 'Set a new password for your account.' },
} as const;

export type AppPagePath = keyof typeof APP_PAGES;

/** `PageMeta` for one of the application's own, unindexed pages. */
export function appPageMeta(path: AppPagePath): PageMeta {
  const entry = APP_PAGES[path];
  return privateMeta({ title: entry.title, path, description: entry.description });
}

/* -------------------------------------------------------------------------- */
/* Resolution                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * What each adventure page calls itself.
 *
 * Every one of them is `noindex` — see the note at the call site — so these
 * titles are for the browser tab and the back button, not for a search result.
 * Anything not listed — the home page itself, a kingdom, a quest, a boss — takes
 * `ADVENTURE_HOME`, which is the honest description of all of them.
 */
const ADVENTURE_HOME = { title: 'Your adventure', description: 'Your quests, level and gold.' };
const ADVENTURE_PAGES: Record<string, { title: string; description: string }> = {
  character: { title: 'Your character', description: 'Your adventurer — class, look and gear.' },
  journal: {
    title: 'Your adventure journal',
    description: 'What has been going wrong lately, and what you have beaten.',
  },
};

/** Strips a query string, a fragment and a trailing slash. */
export function normalisePath(path: string): string {
  const withoutQuery = path.split(/[?#]/)[0] ?? '/';
  if (withoutQuery === '' || withoutQuery === '/') return '/';
  const prefixed = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  return prefixed.replace(/\/+$/, '') || '/';
}

/**
 * The `PageMeta` for any path.
 *
 * An unknown path resolves to the not-found meta, which is `noindex` — the same
 * answer the running application gives, and the reason a mistyped URL cannot be
 * indexed under the homepage's title.
 */
export function metaForPath(
  registry: CurriculumRegistry,
  path: string,
  origin: string = SITE.origin,
): PageMeta {
  const route = normalisePath(path);

  switch (route) {
    case '/':
      return homeMeta(registry, origin);
    case '/courses':
      return coursesMeta(registry, origin);
    case '/map':
      return mapMeta(registry, origin);
    case '/diagnostic':
      return diagnosticMeta(origin);
    case '/pricing':
      return pricingMeta(origin);
    case '/about':
      return aboutMeta(origin);
    case '/privacy':
      return privacyMeta(origin);
    default:
      break;
  }

  if (route in APP_PAGES) return appPageMeta(route as AppPagePath);

  const [, section = '', id = ''] = route.split('/');

  if (section === 'courses' && id !== '') {
    const course = registry.course(id);
    return course ? courseMeta(registry, course, origin) : notFoundMeta(route);
  }
  if (section === 'lessons' && id !== '') {
    const lesson = registry.lesson(id);
    return lesson ? lessonMeta(registry, lesson, origin) : notFoundMeta(route);
  }
  if (section === 'concepts' && id !== '') {
    const concept = registry.concept(id);
    return concept ? conceptMeta(registry, concept, origin) : notFoundMeta(route);
  }
  if (section === 'practice' && id !== '') {
    return practiceMeta(registry, id);
  }
  /*
   * The adventure is one student's game state, so every path under it is
   * `noindex`. Deliberately not in `APP_PAGES`: the prerenderer walks that
   * list, and a feature-flagged route gets no static file until it ships.
   */
  if (section === 'adventure') {
    const page = ADVENTURE_PAGES[id] ?? ADVENTURE_HOME;
    return privateMeta({ title: page.title, path: route, description: page.description });
  }

  return notFoundMeta(route);
}
